import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Typography } from "@mui/material";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AreaTitle from "../../components/areaTitle/AreaTitle";
import useAISimulator from "./hooks/useAISimulator";
import SimulatorMessage from "./components/SimulatorMessage";
import SimulatorComposer from "./components/SimulatorComposer";
import SimulatorEmptyState from "./components/SimulatorEmptyState";
import KnowledgePanel from "./components/KnowledgePanel";
import styles from "./AISimulatorPage.module.css";

const KNOWLEDGE_STORAGE_KEY = "aiSimulatorKnowledge";

/**
 * Simulador de Atendimento por IA (AI Patient Response Engine — Fase 1).
 *
 * Permite conversar como se fosse um paciente e ver como a IA responderia
 * usando as informações da clínica. Ambiente controlado: nenhuma mensagem
 * é enviada para pacientes reais.
 */
function AISimulatorPage() {
  const { messages, isGenerating, error, sendMessage, resetConversation } = useAISimulator();
  const messagesContainerRef = useRef(null);

  const [knowledge, setKnowledge] = useState(() => localStorage.getItem(KNOWLEDGE_STORAGE_KEY) || "");

  const handleKnowledgeChange = (text) => {
    setKnowledge(text);
    localStorage.setItem(KNOWLEDGE_STORAGE_KEY, text);
  };

  const handleSendMessage = (text) => sendMessage(text, knowledge);

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages, isGenerating]);

  return (
    <div className="page-content">
      <div className={styles.pageHeader}>
        <AreaTitle
          Title="Simulador de Atendimento por IA"
          Icon={<SmartToyOutlinedIcon />}
          Description="Converse como se fosse um paciente e veja como a IA responderia usando as informações da sua clínica. Nenhuma mensagem é enviada para pacientes reais."
        />
      </div>

      <KnowledgePanel value={knowledge} onChange={handleKnowledgeChange} />

      <div className={styles.container}>
        <div className={styles.chatHeader}>
          <div className={styles.chatHeaderInfo}>
            <Typography variant="h6" style={{ margin: 0, fontSize: "1.1rem", fontWeight: 500 }}>
              Conversa simulada
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ fontSize: "0.9rem" }}>
              Ambiente de testes — sem envio para pacientes
            </Typography>
          </div>
          <Button
            variant="outlined"
            style={{
              color: "var(--color-blue-main, #5A80FB)",
              borderColor: "var(--color-blue-light3, #BDC9FE)",
              textTransform: "none",
              borderRadius: "8px",
              height: "40px",
            }}
            startIcon={<RestartAltIcon />}
            onClick={resetConversation}
            disabled={messages.length === 0 && !error}
          >
            Reiniciar conversa
          </Button>
        </div>

        <div ref={messagesContainerRef} className={styles.messageContainer}>
          {messages.length === 0 ? (
            <SimulatorEmptyState onSuggestion={handleSendMessage} disabled={isGenerating} />
          ) : (
            messages.map((message, index) => (
              <SimulatorMessage key={index} message={message} />
            ))
          )}

          {isGenerating && (
            <div className={styles.typingBubble}>
              <div className={styles.typingBubbleContent}>A IA está digitando...</div>
            </div>
          )}

          {error && (
            <Alert severity="error" style={{ marginTop: "8px" }}>
              {error}
            </Alert>
          )}
        </div>

        <div className={styles.inputArea}>
          <SimulatorComposer onSend={handleSendMessage} disabled={isGenerating} />
        </div>
      </div>
    </div>
  );
}

export default AISimulatorPage;
